import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { fetchSubscriptionsByRestaurant, deleteSubscriptionsBatch } from "../service/subscriptionService";
import { 
  Plus, 
  Trash2, 
  Edit3, 
  Users, 
  DollarSign, 
  Calendar,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Utensils,
  Clock,
  Package,
  Grid3X3,
  List,
  ChevronDown
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getRestaurantId } from "../utils/JwtUtil";

export default function Subscriptions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const restaurantId = getRestaurantId();
  const qc = useQueryClient();

  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // grid or list

  useEffect(() => {
    document.documentElement.setAttribute(
      "dir",
      i18n.language === "ar" ? "rtl" : "ltr"
    );
  }, [i18n.language]);

  const { data: list = [], isLoading, isError, error } = useQuery({
    queryKey: ["subscriptions", restaurantId],
    queryFn: () => fetchSubscriptionsByRestaurant(restaurantId),
    enabled: !!restaurantId,
  });

  const deleteSub = useMutation({
    mutationFn: deleteSubscriptionsBatch,
    onSuccess: () => {
      qc.invalidateQueries(["subscriptions", restaurantId]);
      setSelectedIds([]);
    },
  });

  const handleDelete = () => {
    if (
      selectedIds.length > 0 &&
      window.confirm(t("deleteConfirm", { count: selectedIds.length }))
    ) {
      deleteSub.mutate(selectedIds);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredSubscriptions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSubscriptions.map(s => s.id));
    }
  };

  const handleSelectSubscription = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  // Filter subscriptions based on search and status
  const filteredSubscriptions = list.filter(subscription => {
    const matchesSearch = !searchTerm || 
      (subscription.nameEn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       subscription.nameAr?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && subscription.isActive) ||
      (statusFilter === "inactive" && !subscription.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (isActive) => {
    return isActive ? (
      <CheckCircle className="w-4 h-4 text-green-600" />
    ) : (
      <XCircle className="w-4 h-4 text-red-600" />
    );
  };

  const getMealTypeIcon = (mealType) => {
    switch (mealType) {
      case 'BREAKFAST': return <Utensils className="w-3 h-3 text-orange-500" />;
      case 'LUNCH': return <Utensils className="w-3 h-3 text-yellow-500" />;
      case 'DINNER': return <Utensils className="w-3 h-3 text-purple-500" />;
      case 'SNACK': return <Package className="w-3 h-3 text-green-500" />;
      default: return <Utensils className="w-3 h-3 text-gray-500" />;
    }
  };

  const SubscriptionCard = ({ subscription }) => {
    const isSelected = selectedIds.includes(subscription.id);
    
    return (
      <div className={`bg-white rounded-xl shadow-sm border-2 transition-all duration-200 hover:shadow-md ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'
      }`}>
        {/* Card Header */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-3 rtl:space-x-reverse flex-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleSelectSubscription(subscription.id)}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate">
                  {i18n.language === 'ar' ? subscription.nameAr || subscription.nameEn : subscription.nameEn || subscription.nameAr}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {i18n.language === 'ar' ? subscription.descriptionAr || subscription.descriptionEn : subscription.descriptionEn || subscription.descriptionAr}
                </p>
              </div>
            </div>
            
            {/* Status Badge */}
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              subscription.isActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {getStatusIcon(subscription.isActive)}
              <span className="ml-1.5 rtl:mr-1.5 rtl:ml-0">
                {subscription.isActive ? t("active") : t("inactive")}
              </span>
            </span>
          </div>

          {/* Key Metrics - Simplified */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600 mx-auto mb-1" />
              <p className="text-xs text-gray-500 mb-1">{t("pricePerDay")}</p>
              <p className="text-sm font-semibold text-gray-900">{subscription.pricePerDay} JD</p>
            </div>
            
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Utensils className="w-5 h-5 text-blue-600 mx-auto mb-1" />
              <p className="text-xs text-gray-500 mb-1">{t("mealsPerDay")}</p>
              <p className="text-sm font-semibold text-gray-900">{subscription.mealsPerDay}</p>
            </div>
            
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Calendar className="w-5 h-5 text-purple-600 mx-auto mb-1" />
              <p className="text-xs text-gray-500 mb-1">{t("duration")}</p>
              <p className="text-sm font-semibold text-gray-900">{subscription.expiryDays}d</p>
            </div>
          </div>

          {/* Meal Types - Simplified */}
          {subscription.mealSpecifications && subscription.mealSpecifications.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-700">{t("availableMealTypes")}</p>
                <span className="text-xs text-gray-500">
                  {subscription.mealSpecifications.length} {subscription.mealSpecifications.length === 1 ? t("type") : t("types")}
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {subscription.mealSpecifications.slice(0, 3).map((spec, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                  >
                    {getMealTypeIcon(spec.mealType)}
                    <span className="ml-1">{t(`mealTypeLabels.${spec.mealType?.toLowerCase()}`) || spec.mealType}</span>
                  </span>
                ))}
                {subscription.mealSpecifications.length > 3 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                    +{subscription.mealSpecifications.length - 3} {t("more")}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Card Footer - Simplified */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 rtl:space-x-reverse text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>{new Date(subscription.updatedAt || subscription.createdAt).toLocaleDateString()}</span>
            </div>
            
            <button
              onClick={() => navigate(`/subscriptions/edit/${subscription.id}`)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Edit3 className="w-3 h-3 mr-1.5 rtl:ml-1.5 rtl:mr-0" />
              {t("edit")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 animate-pulse">
                <div className="p-6">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-full mb-4"></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="h-8 bg-gray-200 rounded"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
                <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-700 font-medium">{error.message}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t("subscriptions")}
              </h1>
              <p className="text-gray-600">
                {t("subscriptionsDescription")}
              </p>
            </div>
            
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <button
                onClick={() => navigate("/subscriptions/create")}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                {t("createSubscription")}
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 rtl:sm:space-x-reverse">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 rtl:right-3 rtl:left-auto top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t("searchSubscriptions")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rtl:pr-10 rtl:pl-3 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              
              {/* Status Filter */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-md px-3 py-2 pr-8 rtl:pl-8 rtl:pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">{t("allStatuses")}</option>
                  <option value="active">{t("active")}</option>
                  <option value="inactive">{t("inactive")}</option>
                </select>
                <ChevronDown className="absolute right-2 rtl:left-2 rtl:right-auto top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
              
              {/* View Mode Toggle */}
              <div className="flex rounded-md shadow-sm">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                    viewMode === "grid"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  } transition-colors`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                    viewMode === "list"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  } transition-colors`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Stats and Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 rtl:sm:space-x-reverse">
              {/* Stats */}
              <div className="text-sm text-gray-600">
                <span className="font-medium">{filteredSubscriptions.length}</span> / <span className="font-medium">{list.length}</span> {t("subscriptions")}
                {selectedIds.length > 0 && (
                  <>
                    <span className="mx-2">•</span>
                    <span className="font-medium text-blue-600">{selectedIds.length}</span> {t("selected")}
                  </>
                )}
              </div>
              
              {/* Bulk Actions */}
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                {selectedIds.length > 0 && (
                  <>
                    <button
                      onClick={handleSelectAll}
                      className="text-xs text-blue-600 hover:text-blue-500 font-medium"
                    >
                      {selectedIds.length === filteredSubscriptions.length ? t("deselectAll") : t("selectAll")}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleteSub.isPending}
                      className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Trash2 className="w-3 h-3 mr-1.5 rtl:ml-1.5 rtl:mr-0" />
                      {deleteSub.isPending ? t("deleting") : t("delete")} ({selectedIds.length})
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {filteredSubscriptions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <div className="text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || statusFilter !== "all" ? t("noSubscriptionsMatch") : t("noSubscriptions")}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm || statusFilter !== "all" 
                  ? t("noSubscriptionsMatchMessage") 
                  : t("noSubscriptionsMessage")
                }
              </p>
              {(!searchTerm && statusFilter === "all") && (
                <button
                  onClick={() => navigate("/subscriptions/create")}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                  {t("createFirstSubscription")}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={`${
            viewMode === "grid" 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
              : "space-y-4"
          }`}>
            {filteredSubscriptions.map((subscription) => (
              <SubscriptionCard key={subscription.id} subscription={subscription} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}